import type { ApiResponse, OpenAiUsagePage } from './types';
import type { ApiKey, ApiKeyUsageDetail, DailyUsageData, ModelUsageDetail, } from './types';
import { formatUtcDate, formatUtcHourKey } from './client';
import { modelPricing } from './pricing';
import { calculateModelCost } from './usage-cost';
import { isOpenAiWebRunUsage } from './web-search';

export type BucketWidth = '1h' | '1d';

export interface NormalizeOptions {
  bucketWidth: BucketWidth;
  fallbackBucketDurationMs: number;
  rangeStart: Date;
  rangeEnd: Date;
}

/** Metadata for one in-range bucket (drives daily-entry creation + coverage window). */
export interface BucketMeta {
  bucketKey: string;
  bucketStartMs: number;
  bucketEndMs: number;
}

/** One usage data point after normalization (cost already resolved). */
export interface NormalizedUsageEvent {
  bucketKey: string;
  bucketDateStr: string;
  model: string;
  apiKeyId?: string;
  projectId?: string;
  contextTokens: number;
  generatedTokens: number;
  cachedTokens: number;
  requests: number;
  modelCost: number;
  pricingFound: boolean;
  usageType: string;
}

export interface NormalizedUsage {
  buckets: BucketMeta[];
  events: NormalizedUsageEvent[];
}

/**
 * Convert a raw usage API response (completions / images / audio / …) into a
 * flat, side-effect-free list of in-range buckets and per-result events.
 * Mirrors the original per-result request-fallback and cost logic exactly.
 */
export function normalizeUsageBuckets(
  usageResponse: ApiResponse<OpenAiUsagePage>,
  usageType: string,
  opts: NormalizeOptions
): NormalizedUsage {
  const buckets: BucketMeta[] = [];
  const events: NormalizedUsageEvent[] = [];

  if (!usageResponse || !usageResponse.success || !usageResponse.data?.data) {
    return { buckets, events };
  }

  const { bucketWidth, fallbackBucketDurationMs, rangeStart, rangeEnd } = opts;

  const filteredBuckets = usageResponse.data.data.filter((bucket) => {
    const bucketStart = new Date(bucket.start_time * 1000);
    return bucketStart >= rangeStart && bucketStart <= rangeEnd;
  });

  for (const bucket of filteredBuckets) {
    const bucketTimestamp = new Date(bucket.start_time * 1000);
    const bucketKey = bucketWidth === '1h'
      ? formatUtcHourKey(bucketTimestamp)
      : formatUtcDate(bucketTimestamp);
    const bucketDateStr = formatUtcDate(bucketTimestamp);

    const bucketStartMs = bucketTimestamp.getTime();
    const bucketEndMs = bucket.end_time
      ? bucket.end_time * 1000
      : bucketStartMs + fallbackBucketDurationMs;

    buckets.push({ bucketKey, bucketStartMs, bucketEndMs });

    for (const result of bucket.results || []) {
      const model = result.model || `unknown-${usageType}`;
      // Try different possible field names for API key ID
      const apiKeyId = result.api_key_id || result.apiKeyId || result.key_id || result.keyId;
      const contextTokens = result.input_tokens || 0;
      const generatedTokens = result.output_tokens || 0;
      const cachedTokens = result.input_cached_tokens || 0;
      const cacheWriteTokens = result.cache_write_tokens || 0;
      const imageCount = Number(result.images ?? result.image_count ?? 0) || 0;
      const audioCharacters = Number(result.characters ?? 0) || 0;
      const audioSeconds = Number(result.seconds ?? 0) || 0;

      const requestCandidates = [
        result.num_model_requests,
        result.numModelRequests,
        result.num_requests,
        result.total_requests,
        result.requests,
        result.n_requests
      ];
      let requests = 0;
      for (const candidate of requestCandidates) {
        if (candidate === undefined || candidate === null) continue;
        const numeric = typeof candidate === 'number' ? candidate : Number(candidate);
        if (Number.isFinite(numeric) && numeric > 0) {
          requests = numeric;
          break;
        }
      }

      const hasTokenUsage = contextTokens > 0 || generatedTokens > 0 || cachedTokens > 0 || cacheWriteTokens > 0;
      const hasOtherUsage = imageCount > 0 || audioCharacters > 0 || audioSeconds > 0;
      const hasAnyUsage = hasTokenUsage || hasOtherUsage;

      if ((requests <= 0 || !Number.isFinite(requests)) && bucketWidth === '1d' && hasAnyUsage) {
        if (usageType === 'images' && imageCount > 0) {
          requests = imageCount;
        } else if (usageType === 'audio_speeches' && audioCharacters > 0) {
          requests = Math.max(1, Math.ceil(audioCharacters / 1000));
        } else if (usageType === 'audio_transcriptions' && audioSeconds > 0) {
          requests = Math.max(1, Math.ceil(audioSeconds / 60));
        } else if (hasTokenUsage) {
          const tokenDerived = Math.round((contextTokens + generatedTokens) / 1000);
          requests = Math.max(1, tokenDerived);
        } else {
          requests = 1;
        }
      }

      if (!hasAnyUsage && (!Number.isFinite(requests) || requests <= 0)) {
        continue;
      }

      if (!Number.isFinite(requests) || requests < 0) {
        requests = 0;
      }
      requests = Math.round(requests);
      const serviceTier = result.service_tier || 'default';
      const batch = result.batch === true;

      // Skip if no requests
      if (requests === 0) continue;

      const { cost: modelCost, pricingFound } = calculateModelCost({
        model,
        usageType,
        serviceTier,
        batch,
        contextTokens,
        generatedTokens,
        cachedTokens,
        cacheWriteTokens,
        requests,
        imageCount,
        audioCharacters,
        audioSeconds,
      });

      events.push({
        bucketKey,
        bucketDateStr,
        model,
        apiKeyId: apiKeyId || undefined,
        projectId: result.project_id || result.projectId || undefined,
        contextTokens,
        generatedTokens,
        cachedTokens,
        requests,
        modelCost,
        pricingFound,
        usageType,
      });
    }
  }

  return { buckets, events };
}

// ── Aggregate ───────────────────────────────────────────────────────────────

export interface UsageAggregate {
  modelUsageMap: Map<string, ModelUsageDetail>;
  apiKeyUsageMap: Map<string, ApiKeyUsageDetail>;
  dailyUsageMap: Map<string, DailyUsageData & { apiKeys: Set<string> }>;
  totals: {
    requests: number;
    cost: number;
    contextTokens: number;
    generatedTokens: number;
    cachedTokens: number;
  };
  earliestBucketStart: number | null;
  latestBucketEnd: number | null;
  unpricedModels: Set<string>;
  projectDayUsageMap: Map<string, ProjectDayUsage>;
}

export interface ProjectDayBucketUsage {
  requests: number;
  webRunRequests: number;
}

export interface ProjectDayKeyUsage {
  apiKeyId: string;
  requests: number;
  webRunRequests: number;
  buckets: Map<string, ProjectDayBucketUsage>;
}

export interface ProjectDayUsage {
  date: string;
  projectId: string;
  keys: Map<string, ProjectDayKeyUsage>;
}

export const OPENAI_UNATTRIBUTED_KEY_ID = 'openai-unattributed';

/**
 * Seed the per-API-key usage rows from the org's API key metadata.
 * Each key starts at zero usage; usage is folded in during aggregation.
 */
export function initApiKeyUsageMap(allApiKeys: ApiKey[]): Map<string, ApiKeyUsageDetail> {
  const apiKeyUsageMap = new Map<string, ApiKeyUsageDetail>();
  for (const apiKeyDetail of allApiKeys) {
    const projectId = apiKeyDetail.projectId;
    const projectName = apiKeyDetail.projectName;
    const ownerType = apiKeyDetail.owner?.type;
    const ownerUserId = ownerType === 'user'
      ? apiKeyDetail.owner.user?.id
      : apiKeyDetail.owner?.service_account?.id;
    const ownerEmail = ownerType === 'user'
      ? apiKeyDetail.owner.user?.email
      : undefined;

    apiKeyUsageMap.set(apiKeyDetail.id, {
      id: apiKeyDetail.id,
      name: apiKeyDetail.name,
      requests: 0,
      numberOfRequests: 0,
      days: 0,
      firstSeen: apiKeyDetail.created_at != null ? new Date(apiKeyDetail.created_at * 1000).toISOString().split('T')[0] : 'N/A',
      lastSeen: apiKeyDetail.last_used_at != null ? new Date(apiKeyDetail.last_used_at * 1000).toISOString().split('T')[0] : 'N/A',
      contextTokens: 0,
      generatedTokens: 0,
      cachedTokens: 0,
      cost: 0,
      cachedCost: 0,
      cachedPercentage: 0,
      costPer100Requests: 0,
      status: 'Active', // 仮の初期値
      modelCosts: {},
      dailyModelCosts: {},
      modelUsage: {},
      projectId: projectId,
      projectName: projectName,
      projectIds: projectId ? [projectId] : undefined,
      provider: 'openai',
      sourceIds: [apiKeyDetail.id],
      ownerUserId,
      ownerEmail,
      ownerType,
      ownerIdentityTrusted: false,
      costQuality: 'calculated'
    });
  }
  return apiKeyUsageMap;
}

export function createUsageAggregate(apiKeyUsageMap: Map<string, ApiKeyUsageDetail>): UsageAggregate {
  return {
    modelUsageMap: new Map(),
    apiKeyUsageMap,
    dailyUsageMap: new Map(),
    totals: { requests: 0, cost: 0, contextTokens: 0, generatedTokens: 0, cachedTokens: 0 },
    earliestBucketStart: null,
    latestBucketEnd: null,
    unpricedModels: new Set(),
    projectDayUsageMap: new Map(),
  };
}

export function createUnresolvedApiKeyUsage(apiKeyId: string, bucketDate: string): ApiKeyUsageDetail {
  const isUnattributed = apiKeyId === OPENAI_UNATTRIBUTED_KEY_ID;
  return {
    id: apiKeyId,
    name: isUnattributed
      ? 'Unattributed OpenAI usage'
      : `Unknown or deleted API key (${apiKeyId})`,
    requests: 0,
    numberOfRequests: 0,
    days: 1,
    firstSeen: bucketDate,
    lastSeen: bucketDate,
    contextTokens: 0,
    generatedTokens: 0,
    cachedTokens: 0,
    cost: 0,
    cachedCost: 0,
    cachedPercentage: 0,
    costPer100Requests: 0,
    status: 'Active',
    modelCosts: {},
    dailyModelCosts: {},
    modelUsage: {},
    provider: 'openai',
    sourceIds: [apiKeyId],
    costQuality: 'calculated'
  };
}

export function ensureDailyEntry(
  aggregate: UsageAggregate,
  bucketKey: string
): DailyUsageData & { apiKeys: Set<string> } {
  let dailyData = aggregate.dailyUsageMap.get(bucketKey);
  if (!dailyData) {
    dailyData = {
      date: bucketKey,
      requests: 0,
      cost: 0,
      contextTokens: 0,
      generatedTokens: 0,
      cachedTokens: 0,
      models: {},
      uniqueApiKeys: 0,
      apiKeys: new Set<string>(),
      provider: 'openai',
      isEstimatedRequests: false,
      breakdowns: {
        openai: {
          provider: 'openai',
          requests: 0,
          cost: 0,
          contextTokens: 0,
          generatedTokens: 0,
          cachedTokens: 0,
          isEstimatedRequests: false
        }
      }
    } as DailyUsageData & { apiKeys: Set<string> };
    aggregate.dailyUsageMap.set(bucketKey, dailyData);
  }
  return dailyData;
}

export const buildProjectDayKey = (date: string, projectId: string): string => `${date}\u0000${projectId}`;

function recordProjectDayCompletionUsage(
  aggregate: UsageAggregate,
  event: NormalizedUsageEvent,
  resolvedApiKeyId: string,
  resolvedProjectId: string
): void {
  if (event.usageType !== 'completions') return;

  const mapKey = buildProjectDayKey(event.bucketDateStr, resolvedProjectId);
  let projectDayUsage = aggregate.projectDayUsageMap.get(mapKey);
  if (!projectDayUsage) {
    projectDayUsage = {
      date: event.bucketDateStr,
      projectId: resolvedProjectId,
      keys: new Map(),
    };
    aggregate.projectDayUsageMap.set(mapKey, projectDayUsage);
  }

  let keyUsage = projectDayUsage.keys.get(resolvedApiKeyId);
  if (!keyUsage) {
    keyUsage = {
      apiKeyId: resolvedApiKeyId,
      requests: 0,
      webRunRequests: 0,
      buckets: new Map(),
    };
    projectDayUsage.keys.set(resolvedApiKeyId, keyUsage);
  }

  const isWebRun = isOpenAiWebRunUsage(event.model, event.usageType);
  keyUsage.requests += event.requests;
  if (isWebRun) keyUsage.webRunRequests += event.requests;

  const bucketUsage = keyUsage.buckets.get(event.bucketKey) ?? {
    requests: 0,
    webRunRequests: 0,
  };
  bucketUsage.requests += event.requests;
  if (isWebRun) bucketUsage.webRunRequests += event.requests;
  keyUsage.buckets.set(event.bucketKey, bucketUsage);
}

/**
 * Fold one normalized usage payload (buckets + events) into the aggregate.
 * Bucket metadata first (creates daily entries, extends the coverage window),
 * then per-result events (model/apiKey/daily sums).
 */
export function aggregateNormalized(aggregate: UsageAggregate, normalized: NormalizedUsage): void {
  for (const meta of normalized.buckets) {
    if (aggregate.earliestBucketStart === null || meta.bucketStartMs < aggregate.earliestBucketStart) {
      aggregate.earliestBucketStart = meta.bucketStartMs;
    }
    if (aggregate.latestBucketEnd === null || meta.bucketEndMs > aggregate.latestBucketEnd) {
      aggregate.latestBucketEnd = meta.bucketEndMs;
    }
    ensureDailyEntry(aggregate, meta.bucketKey);
  }

  for (const event of normalized.events) {
    const {
      model,
      apiKeyId,
      contextTokens,
      generatedTokens,
      cachedTokens,
      requests,
      modelCost,
      pricingFound,
      bucketKey,
      bucketDateStr,
      projectId,
    } = event;
    const dailyData = ensureDailyEntry(aggregate, bucketKey);

    // Usage can refer to a deleted/inaccessible key, or omit api_key_id. Keep it
    // in an unresolved synthetic row so per-key budgets still reconcile to usage.
    const resolvedApiKeyId = apiKeyId || OPENAI_UNATTRIBUTED_KEY_ID;
    if (!aggregate.apiKeyUsageMap.has(resolvedApiKeyId)) {
      aggregate.apiKeyUsageMap.set(
        resolvedApiKeyId,
        createUnresolvedApiKeyUsage(resolvedApiKeyId, bucketDateStr)
      );
    }
    const apiKeyUsage = aggregate.apiKeyUsageMap.get(resolvedApiKeyId)!;
    const resolvedProjectId = projectId || apiKeyUsage.projectId;
    if (resolvedProjectId) {
      recordProjectDayCompletionUsage(aggregate, event, resolvedApiKeyId, resolvedProjectId);
    }

    // Add API key to daily set for unique counting
    dailyData.apiKeys.add(resolvedApiKeyId);

    // モデルごとの集計
    if (!aggregate.modelUsageMap.has(model)) {
      aggregate.modelUsageMap.set(model, {
        model,
        contextTokens: 0,
        generatedTokens: 0,
        cachedTokens: 0,
        cost: 0,
        numberOfRequests: 0,
        provider: 'openai'
      });
    }
    const currentModelUsage = aggregate.modelUsageMap.get(model)!;
    if (!currentModelUsage.lastUsed || bucketDateStr > currentModelUsage.lastUsed) {
      currentModelUsage.lastUsed = bucketDateStr;
    }
    currentModelUsage.contextTokens += contextTokens;
    currentModelUsage.generatedTokens += generatedTokens;
    currentModelUsage.cachedTokens += cachedTokens;
    currentModelUsage.numberOfRequests += requests;
    currentModelUsage.cost += modelCost;

    // APIキー別の集計
    {
      if (apiKeyUsage.firstSeen === 'N/A' || bucketDateStr < apiKeyUsage.firstSeen) {
        apiKeyUsage.firstSeen = bucketDateStr;
      }
      if (apiKeyUsage.lastSeen === 'N/A' || bucketDateStr > apiKeyUsage.lastSeen) {
        apiKeyUsage.lastSeen = bucketDateStr;
      }
      apiKeyUsage.contextTokens += contextTokens;
      apiKeyUsage.generatedTokens += generatedTokens;
      apiKeyUsage.cachedTokens += cachedTokens;
      apiKeyUsage.requests += requests;
      apiKeyUsage.cost += modelCost;
      if (!pricingFound) {
        apiKeyUsage.costQuality = 'estimated';
        aggregate.unpricedModels.add(model);
      }

      if (!apiKeyUsage.modelUsage[model]) {
        apiKeyUsage.modelUsage[model] = {
          model,
          contextTokens: 0,
          generatedTokens: 0,
          cachedTokens: 0,
          cost: 0,
          numberOfRequests: 0,
          provider: 'openai'
        };
      }
      apiKeyUsage.modelUsage[model].contextTokens += contextTokens;
      apiKeyUsage.modelUsage[model].generatedTokens += generatedTokens;
      apiKeyUsage.modelUsage[model].cachedTokens += cachedTokens;
      apiKeyUsage.modelUsage[model].numberOfRequests += requests;
      apiKeyUsage.modelUsage[model].cost += modelCost;
      if (!apiKeyUsage.modelUsage[model].lastUsed || bucketDateStr > apiKeyUsage.modelUsage[model].lastUsed!) {
        apiKeyUsage.modelUsage[model].lastUsed = bucketDateStr;
      }

      if (!apiKeyUsage.modelCosts[model]) {
        apiKeyUsage.modelCosts[model] = 0;
      }
      apiKeyUsage.modelCosts[model] += modelCost;

      if (!apiKeyUsage.dailyModelCosts) apiKeyUsage.dailyModelCosts = {};
      if (!apiKeyUsage.dailyModelCosts[bucketDateStr]) apiKeyUsage.dailyModelCosts[bucketDateStr] = {};
      apiKeyUsage.dailyModelCosts[bucketDateStr][model] =
        (apiKeyUsage.dailyModelCosts[bucketDateStr][model] ?? 0) + modelCost;
    }

    aggregate.totals.requests += requests;
    aggregate.totals.cost += modelCost;
    aggregate.totals.contextTokens += contextTokens;
    aggregate.totals.generatedTokens += generatedTokens;
    aggregate.totals.cachedTokens += cachedTokens;

    if (!dailyData.breakdowns) {
      dailyData.breakdowns = {};
    }
    if (!dailyData.breakdowns.openai) {
      dailyData.breakdowns.openai = {
        provider: 'openai',
        requests: 0,
        cost: 0,
        contextTokens: 0,
        generatedTokens: 0,
        cachedTokens: 0,
        isEstimatedRequests: false
      };
    }
    const openaiBreakdown = dailyData.breakdowns.openai!;
    openaiBreakdown.requests += requests;
    openaiBreakdown.cost += modelCost;
    openaiBreakdown.contextTokens += contextTokens;
    openaiBreakdown.generatedTokens += generatedTokens;
    openaiBreakdown.cachedTokens += cachedTokens;

    dailyData.requests += requests;
    dailyData.cost += modelCost;
    dailyData.contextTokens += contextTokens;
    dailyData.generatedTokens += generatedTokens;
    dailyData.cachedTokens += cachedTokens;

    if (!dailyData.models[model]) {
      dailyData.models[model] = 0;
    }
    dailyData.models[model] += requests;
    if (!dailyData.modelCosts) dailyData.modelCosts = {};
    dailyData.modelCosts[model] = (dailyData.modelCosts[model] ?? 0) + modelCost;
  }
}

/**
 * Apply the daily cost report (1d only): override each day's cost with the
 * authoritative cost-report amount and adjust the running total by the delta.
 * Per-model / per-key costs remain their detailed estimates except for line
 * items (such as GPT-Live 1) reconciled directly from the same Costs payload.
 */
export function applyDailyCostReport(aggregate: UsageAggregate, costsUsage: ApiResponse<OpenAiUsagePage> | null): void {
  if (!costsUsage?.success || !costsUsage.data?.data) return;

  costsUsage.data.data.forEach((bucket) => {
    const startSeconds = bucket.start_time ?? bucket.startTime;
    if (!Number.isFinite(startSeconds)) return;
    const bucketStart = new Date(startSeconds * 1000);
    const bucketKey = formatUtcDate(bucketStart);
    const bucketResults = Array.isArray(bucket.results) ? bucket.results : [];
    const bucketCost = bucketResults.reduce((sum, result) => {
      const amountValue = result?.amount?.value;
      const numeric = typeof amountValue === 'number' ? amountValue : Number(amountValue);
      return Number.isFinite(numeric) ? sum + numeric : sum;
    }, 0);

    if (!Number.isFinite(bucketCost)) {
      return;
    }

    let dailyData = aggregate.dailyUsageMap.get(bucketKey);
    if (!dailyData) {
      dailyData = {
        date: bucketKey,
        requests: 0,
        cost: 0,
        contextTokens: 0,
        generatedTokens: 0,
        cachedTokens: 0,
        models: {},
        uniqueApiKeys: 0,
        apiKeys: new Set<string>(),
        provider: 'openai',
        num_requests: 0,
        breakdowns: {
          openai: {
            provider: 'openai',
            requests: 0,
            cost: 0,
            contextTokens: 0,
            generatedTokens: 0,
            cachedTokens: 0,
            isEstimatedRequests: false
          }
        },
        isEstimatedRequests: false
      } as DailyUsageData & { apiKeys: Set<string> };
      aggregate.dailyUsageMap.set(bucketKey, dailyData);
    }

    const previousCost = dailyData.cost ?? 0;
    dailyData.cost = bucketCost;

    if (!dailyData.breakdowns) {
      dailyData.breakdowns = {};
    }
    if (!dailyData.breakdowns.openai) {
      dailyData.breakdowns.openai = {
        provider: 'openai',
        requests: dailyData.requests ?? 0,
        cost: 0,
        contextTokens: dailyData.contextTokens ?? 0,
        generatedTokens: dailyData.generatedTokens ?? 0,
        cachedTokens: dailyData.cachedTokens ?? 0,
        isEstimatedRequests: false
      };
    }
    dailyData.breakdowns.openai.cost = bucketCost;

    aggregate.totals.cost += bucketCost - previousCost;
  });
}

/** Derive per-key rollup metrics (usage period, cached %, cost/100 req). */
export function finalizeApiKeyMetrics(apiKeyUsageMap: Map<string, ApiKeyUsageDetail>): void {
  apiKeyUsageMap.forEach(apiKeyDetail => {
    const firstSeenDate = new Date(apiKeyDetail.firstSeen);
    const lastSeenDate = new Date(apiKeyDetail.lastSeen);
    const diffTime = Math.abs(lastSeenDate.getTime() - firstSeenDate.getTime());
    const diffDays = Number.isFinite(diffTime)
      ? Math.max(1, Math.ceil(diffTime / (1000 * 60 * 60 * 24))) : 0;
    apiKeyDetail.days = diffDays;

    apiKeyDetail.numberOfRequests = apiKeyDetail.requests;

    apiKeyDetail.cachedPercentage = apiKeyDetail.contextTokens > 0 ?
      (apiKeyDetail.cachedTokens / apiKeyDetail.contextTokens) * 100 : 0;
    apiKeyDetail.costPer100Requests = apiKeyDetail.requests > 0 ?
      (apiKeyDetail.cost / apiKeyDetail.requests) * 100 : 0;
    // モデル別 cached tokens × cached-input 単価で算出（tier 割引は近似として未適用）
    let cachedCost = 0;
    Object.values(apiKeyDetail.modelUsage).forEach(modelStat => {
      const pricing = modelPricing[modelStat.model];
      if (pricing) {
        cachedCost += modelStat.cachedTokens * (pricing.cachedInput ?? pricing.input);
      }
    });
    apiKeyDetail.cachedCost = cachedCost;
  });
}

/** Materialize the daily usage list: resolve unique API key counts and sort by date. */
export function buildDailyUsageArray(
  dailyUsageMap: Map<string, DailyUsageData & { apiKeys: Set<string> }>
): DailyUsageData[] {
  return Array.from(dailyUsageMap.values())
    .map(day => {
      const uniqueApiKeyCount = day.apiKeys.size;
      return {
        ...day,
        uniqueApiKeys: uniqueApiKeyCount,
        apiKeys: Array.from(day.apiKeys) // Preserve API key activity for finer-grained charts
      };
    })
    .sort((a, b) => a.date.localeCompare(b.date));
}

/** Compute the coverage window (observed vs expected hours) for the requested range. */
export function computeDataWindow(
  earliestBucketStart: number | null,
  latestBucketEnd: number | null,
  dateRange: { startDate: Date; endDate: Date }
) {
  const coverageHoursRaw = earliestBucketStart !== null && latestBucketEnd !== null
    ? (latestBucketEnd - earliestBucketStart) / (1000 * 60 * 60)
    : 0;
  const expectedCoverageHoursRaw = Math.max(0, (dateRange.endDate.getTime() - dateRange.startDate.getTime() + 1) / (1000 * 60 * 60));
  const coverageHours = Number(Math.min(expectedCoverageHoursRaw, Math.max(0, coverageHoursRaw)).toFixed(2));
  const expectedHours = Number(expectedCoverageHoursRaw.toFixed(2));
  return {
    start: (earliestBucketStart !== null ? new Date(earliestBucketStart) : dateRange.startDate).toISOString(),
    end: (latestBucketEnd !== null ? new Date(latestBucketEnd) : dateRange.endDate).toISOString(),
    timezoneOffsetMinutes: -dateRange.startDate.getTimezoneOffset(),
    coverageHours,
    expectedHours
  };
}
